import {
  AfterContentInit,
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
  inject,
  input,
  Signal,
  computed,
  AfterViewInit,
  WritableSignal,
  signal,
} from '@angular/core';
import { environment } from '@app/src/environments/environment';
import { DebounceService, DeviceManagerService, PROVIDER_NAMES, Translation } from '@ckmk/angular';
import { FaIconComponent, IconDefinition } from '@fortawesome/angular-fontawesome';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { ServiceCategoryModel } from '@app/src/app/modules/service/classes/category/model/service.category.model';
import { SolutionCategoryModel } from '@app/src/app/modules/solution/classes/category/model/solution.category.model';
import { SolutionCategoryMenuCard } from '@app/src/app/modules/solution/components/solution-category-menu-card/solution-category-menu-card';
import { ServiceCategoryMenuCard } from '@app/src/app/modules/service/components/service-category-menu-card/service-category-menu-card';
import { filter, Subscription } from 'rxjs';

@Component({
  selector: 'front-menu',
  standalone: true,
  imports: [
    FaIconComponent,
    RouterLink,
    Translation,
    SolutionCategoryMenuCard,
    ServiceCategoryMenuCard,
  ],
  templateUrl: './front-menu.html',
  styleUrl: './front-menu.scss',
  host: {
    '(window:scroll)': 'onScroll($event)',
  },
  encapsulation: ViewEncapsulation.None,
})
export class FrontMenu implements OnInit, OnChanges, OnDestroy, AfterContentInit, AfterViewInit {
  protected readonly self = inject<ElementRef<HTMLElement>>(ElementRef);

  protected readonly deviceService = inject(DeviceManagerService);

  protected readonly router = inject(Router);

  protected readonly icons = inject(PROVIDER_NAMES.ICONS);

  protected readonly debouncer = new DebounceService();

  public readonly serviceCategories = input.required<ServiceCategoryModel[]>();

  public readonly solutionCategories = input.required<SolutionCategoryModel[]>();

  protected readonly contentMobile: WritableSignal<boolean> = signal<boolean>(false);

  protected readonly menuOpen: WritableSignal<boolean> = signal<boolean>(false);

  protected readonly currentUrl: WritableSignal<string> = signal<string>('/');

  protected readonly subscriptions: WritableSignal<Subscription[]> = signal<Subscription[]>([]);

  protected readonly siteName: Signal<string> = computed<string>(() => environment.SITE_NAME);

  protected readonly topEnable: Signal<number> = computed<number>(() => 60);

  protected readonly topDisable: Signal<number> = computed<number>(() => this.topEnable() / 2);

  protected readonly localItems: Signal<any[]> = computed<any[]>(() => [
    {
      name: 'home',
      link: '/home',
      label: 'Home',
      items: computed<any[]>(() => []),
      activated: computed<boolean>(
        () => this.currentUrl().endsWith('/home') || this.currentUrl() === '/',
      ),
      isHidden: computed<boolean>(() => false),
    },
    // {
    //   name: 'about-us',
    //   link: '/about-us',
    //   label: 'About us',
    //   items: computed<any[]>(() => []),
    //   activated: computed<boolean>(() => this._router.url.includes('/about-us')),
    //   isHidden: computed<boolean>(() => false),
    // },
    {
      name: 'services',
      label: 'Services',
      subLabel: 'Service categories',
      subItemsVisible: signal<boolean>(false),
      items: computed<any[]>(() => this.serviceCategories()),
      activated: computed<boolean>(() => this.currentUrl().includes('/services')),
      isHidden: computed<boolean>(() => this.serviceCategories().length < 1),
    },
    {
      name: 'solutions',
      label: 'Solutions',
      subLabel: 'Solution categories',
      subItemsVisible: signal<boolean>(false),
      items: computed<any[]>(() => this.solutionCategories()),
      activated: computed<boolean>(() => this.currentUrl().includes('/solutions')),
      isHidden: computed<boolean>(() => this.solutionCategories().length < 1),
    },
    {
      name: 'rse',
      link: '/rse',
      label: 'RSE',
      items: computed<any[]>(() => []),
      activated: computed<boolean>(() => this.currentUrl().endsWith('/rse')),
      isHidden: computed<boolean>(() => false),
    },
    {
      name: 'blog',
      link: '/blog',
      label: 'Blog',
      items: computed<any[]>(() => []),
      activated: computed<boolean>(() => this.currentUrl().includes('/blog')),
      isHidden: computed<boolean>(() => false),
    },
    {
      name: 'contact',
      link: '/contact',
      label: 'Contact',
      items: computed<any[]>(() => []),
      activated: computed<boolean>(() => this.currentUrl().endsWith('/contact')),
      isHidden: computed<boolean>(() => false),
    },
  ]);

  protected readonly localIcon: Signal<IconDefinition> = computed(() => {
    if (this.menuOpen()) {
      return this.icons.close;
    }

    return this.icons.burger;
  });

  public constructor() {}

  public ngOnInit() {
    this.currentUrl.set(this.router.url);

    this.subscriptions.set([
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe({
        next: (event) => {
          if (this.menuOpen()) {
            this.toggleMenu();
          } else {
            this.closeSubItemsContent();
          }

          this.currentUrl.set(event.url);
        },
      }),
    ]);
  }

  public ngOnChanges(changes: SimpleChanges) {}

  public ngOnDestroy() {
    this.subscriptions.update((list) => list.filter((sub) => sub.unsubscribe()));
  }

  public ngAfterContentInit() {}

  public ngAfterViewInit() {
    Promise.resolve().then(() => {
      this.contentMobile.set(this.deviceService.isMobile());
    });
  }

  protected toggleMenu(): void {
    this.menuOpen.update((val) => !val);

    const body = document.body;

    if (this.menuOpen()) {
      if (this.contentMobile()) {
        body.classList.add('is-fixed');
      }
    } else {
      if (this.contentMobile()) {
        body.classList.remove('is-fixed');
      }

      this.closeSubItemsContent();
    }
  }

  protected toggleSubMenu(event: PointerEvent, item: any): boolean {
    event.stopPropagation();

    if (this.contentMobile() && item.subItemsVisible) {
      item.subItemsVisible.update((val: boolean) => !val);
    }

    return this.contentMobile();
  }

  protected toggleSubMenuByFocus(event: PointerEvent, item: any, value: boolean): void {
    if (!this.contentMobile()) {
      if (value) {
        this.debouncer.clearTimeOut();

        if (item.subItemsVisible) {
          item.subItemsVisible.set(true);
        }

        this.closeSubItemsContent(item);
      } else {
        this.debouncer.timeOut(() => {
          if (item.subItemsVisible) {
            item.subItemsVisible.set(false);
          }
        }, 300);
      }
    }
  }

  protected closeSubItemsContent(item?: any): void {
    this.localItems().forEach((it) => {
      if (it.subItemsVisible && it.subItemsVisible() && it !== item) {
        it.subItemsVisible.set(false);
      }
    });
  }

  protected onScroll(event: Event): void {
    const element = this.self.nativeElement;

    if (window.scrollY >= this.topEnable()) {
      if (!element.classList.contains('on-scroll')) {
        element.classList.add('on-scroll');
      }
    } else if (window.scrollY <= this.topDisable()) {
      if (element.classList.contains('on-scroll')) {
        element.classList.remove('on-scroll');
      }
    }
  }
}
